import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { timeEntryService } from '../../services/api';
import { formatHoursMinutes } from '../../utils/formatHours';

// Styled components
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0;
`;

const AlertBox = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: ${({ variant }) => (variant === 'error' ? '#F8D7DA' : '#D4EDDA')};
  color: ${({ theme, variant }) =>
    variant === 'error' ? theme.colors.error : theme.colors.success};
`;

const DetailItem = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.medium};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const DetailValue = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  line-height: 1.5;
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;
  color: white;

  background-color: ${({ status, theme }) => {
    if (status === 'approved') return theme.colors.success;
    if (status === 'challenged') return theme.colors.error;
    return theme.colors.warning;
  }};
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  @media (max-width: 576px) {
    /* Sticky bottom action bar: full-width Approve (primary, on top) + Challenge */
    position: sticky;
    bottom: 0;
    z-index: ${({ theme }) => theme.zIndex['10']};
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[3]};
    padding-bottom: calc(${({ theme }) => theme.spacing[3]} + env(safe-area-inset-bottom, 0px));
    background-color: ${({ theme }) => theme.colors.white};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);

    & > * {
      width: 100%;
      min-height: 44px;
    }
  }
`;

const HistoryItem = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing[3]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const HistoryDate = styled.div`
  min-width: 180px;
  color: ${({ theme }) => theme.colors.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  @media (max-width: 576px) {
    min-width: 0;
  }
`;

const HistoryAction = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.dark};
`;

const HistoryNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-style: italic;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 576px) {
    /* Bottom-sheet modal: stacked full-width buttons, primary on top */
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing[2]};

    & > * {
      width: 100%;
    }
  }
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  min-height: 100px;
  resize: vertical;
  margin-top: ${({ theme }) => theme.spacing[2]};

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: var(--focus-act);
  }
`;

const ModalError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

// Start/stop breakdown (REQ #17)
const SegRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.dark};
  &:last-child { border-bottom: none; }
`;
const FieldLabel = styled.label`
  display: block;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.dark};
  margin-top: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
const HoursInput = styled.input`
  width: 140px;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin-top: ${({ theme }) => theme.spacing[2]};
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; outline: none; box-shadow: var(--focus-act); }
`;

// The challenge field stays DECIMAL hours (challengeTimeEntry takes decimal
// hours); the H:MM reading is echoed beside it so it agrees with the entry's
// duration shown above.
const HoursEquiv = styled.span`
  margin-left: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

// entry.date is a plain 'YYYY-MM-DD' string — parse the parts to avoid timezone shifts
const formatDate = (dateString) => {
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day)
      .toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return dateString;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ApprovalDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [timeEntry, setTimeEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeReason, setChallengeReason] = useState('');
  const [challengeHours, setChallengeHours] = useState('');
  const [modalError, setModalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const canDecide = Boolean(timeEntry?.finishedAt) && timeEntry.status === 'pending';

  const fetchEntry = useCallback(async () => {
    const entry = await timeEntryService.getApprovalById(id);
    // Fail closed for older or unexpected responses that still expose a draft.
    if (!entry?.finishedAt) {
      setTimeEntry(null);
      throw new Error('Time entry has not been finished');
    }
    setTimeEntry(entry);
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setTimeEntry(null);
        setShowApproveModal(false);
        setShowChallengeModal(false);
        await fetchEntry();
      } catch (err) {
        setError(t('customer.approvalDetail.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [fetchEntry]);

  const handleApprove = async () => {
    if (!canDecide) return;
    try {
      setSubmitting(true);
      setModalError(null);
      const result = await timeEntryService.approveTimeEntry(id);
      setShowApproveModal(false);
      setSuccess(result?.message || t('customer.approvalDetail.approveSuccess'));
      setError(null);
      await fetchEntry();
    } catch (err) {
      setModalError(t('customer.approvalDetail.approveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChallenge = async () => {
    if (!canDecide) return;
    if (!challengeReason.trim()) {
      setModalError(t('customer.approvals.challengeReasonRequired'));
      return;
    }
    const h = Number(challengeHours);
    if (challengeHours !== '' && (!Number.isFinite(h) || h < 0 || h > 24)) {
      setModalError(t('customer.approvals.hoursRange', { defaultValue: 'Approved hours must be between 0 and 24' }));
      return;
    }

    try {
      setSubmitting(true);
      setModalError(null);
      const result = await timeEntryService.challengeTimeEntry(id, challengeReason.trim(), challengeHours === '' ? undefined : h);
      setShowChallengeModal(false);
      setChallengeReason('');
      setSuccess(result?.message || t('customer.approvalDetail.challengeSuccess'));
      setError(null);
      await fetchEntry();
    } catch (err) {
      setModalError(t('customer.approvalDetail.challengeError'));
    } finally {
      setSubmitting(false);
    }
  };

  const openChallengeModal = () => {
    if (!canDecide) return;
    setChallengeReason('');
    // Decimal on purpose: the field feeds the API's decimal `hours` parameter.
    setChallengeHours(timeEntry ? String(timeEntry.hours ?? '') : '');
    setModalError(null);
    setShowChallengeModal(true);
  };

  const openApproveModal = () => {
    if (!canDecide) return;
    setModalError(null);
    setShowApproveModal(true);
  };

  if (loading) {
    return <div>{t('customer.approvalDetail.loading')}</div>;
  }

  if (error && !timeEntry) {
    return (
      <>
        <AlertBox variant="error">{error}</AlertBox>
        <Button variant="outline" onClick={() => navigate('/customer/approvals')}>
          {t('customer.approvalDetail.backToApprovals')}
        </Button>
      </>
    );
  }

  const status = timeEntry?.status || 'pending';

  return (
    <>
      <PageHeader>
        <Title>{t('customer.approvalDetail.title')}</Title>
        <Button variant="outline" size="sm" onClick={() => navigate('/customer/approvals')}>
          {t('customer.approvalDetail.backToApprovals')}
        </Button>
      </PageHeader>

      {error && <AlertBox variant="error">{error}</AlertBox>}
      {success && <AlertBox variant="success">{success}</AlertBox>}

      {timeEntry && (
        <>
          <Card padding="md" style={{ marginBottom: '1.5rem' }}>
            <DetailItem>
              <DetailLabel>{t('customer.approvalDetail.currentStatus')}</DetailLabel>
              <DetailValue>
                <Status status={status}>{t(`status.${status}`)}</Status>
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{t('common.employee')}</DetailLabel>
              <DetailValue>{timeEntry.employeeName || timeEntry.employee?.name}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{t('common.date')}</DetailLabel>
              <DetailValue>{formatDate(timeEntry.date)}</DetailValue>
            </DetailItem>

            {/* Start/stop breakdown so the customer can review the day (REQ #17) */}
            {timeEntry.isDayOff ? (
              <DetailItem>
                <DetailLabel>{t('customer.approvals.workPeriod', { defaultValue: 'Work' })}</DetailLabel>
                <DetailValue>{t('customer.approvals.freeDay', { defaultValue: 'Free (off duty)' })}</DetailValue>
              </DetailItem>
            ) : timeEntry.segments && (timeEntry.segments.work || timeEntry.segments.breaks) ? (
              <DetailItem>
                <DetailLabel>{t('customer.approvals.times', { defaultValue: 'Start / stop times' })}</DetailLabel>
                <DetailValue>
                  {(timeEntry.segments.work || []).map((w, i) => (
                    <SegRow key={`w${i}`}>
                      <span>{t('customer.approvals.workPeriod', { defaultValue: 'Work' })} {i + 1}</span>
                      <span>{w.start || '—'} → {w.end || '—'}</span>
                    </SegRow>
                  ))}
                  {(timeEntry.segments.breaks || []).map((b, i) => (
                    <SegRow key={`b${i}`} style={{ opacity: 0.7 }}>
                      <span>{t('customer.approvals.breakPeriod', { defaultValue: 'Break' })} {i + 1}</span>
                      <span>{b.start || '—'} → {b.end || '—'}</span>
                    </SegRow>
                  ))}
                </DetailValue>
              </DetailItem>
            ) : null}

            <DetailItem>
              <DetailLabel>{t('common.hours')}</DetailLabel>
              <DetailValue>{timeEntry.durationFormatted || formatHoursMinutes(timeEntry.hours)}</DetailValue>
            </DetailItem>

            {timeEntry.description ? (
              <DetailItem>
                <DetailLabel>{t('common.description')}</DetailLabel>
                <DetailValue>{timeEntry.description}</DetailValue>
              </DetailItem>
            ) : null}

            {timeEntry.challengeReason && (
              <DetailItem>
                <DetailLabel>{t('customer.approvals.challengeReason')}</DetailLabel>
                <DetailValue style={{ color: 'var(--act-danger)' }}>{timeEntry.challengeReason}</DetailValue>
              </DetailItem>
            )}
          </Card>

          {canDecide && (
            <ActionsRow>
              <Button variant="accent" onClick={openChallengeModal}>
                {t('customer.approvalDetail.challenge')}
              </Button>
              <Button variant="primary" onClick={openApproveModal}>
                {t('customer.approvalDetail.approve')}
              </Button>
            </ActionsRow>
          )}

          {Array.isArray(timeEntry.history) && timeEntry.history.length > 0 && (
            <Card title={t('customer.approvalDetail.statusHistory')} padding="md">
              {timeEntry.history.map((item, index) => (
                <HistoryItem key={index}>
                  <HistoryDate>{formatDateTime(item.timestamp)}</HistoryDate>
                  <HistoryAction>
                    <div>{item.action}</div>
                    {item.note && <HistoryNote>{item.note}</HistoryNote>}
                  </HistoryAction>
                </HistoryItem>
              ))}
            </Card>
          )}

          {/* Approve confirmation modal */}
          {showApproveModal && canDecide && (
            <Modal title={t('customer.approvalDetail.confirmApprovalTitle')} onClose={() => setShowApproveModal(false)} size="sm">
              <p>{t('customer.approvalDetail.confirmApproveText')}</p>
              {modalError && <ModalError>{modalError}</ModalError>}
              <ModalActions>
                <Button
                  variant="outline"
                  onClick={() => setShowApproveModal(false)}
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleApprove} disabled={submitting}>
                  {submitting ? t('customer.approvalDetail.approving') : t('customer.approvalDetail.approve')}
                </Button>
              </ModalActions>
            </Modal>
          )}

          {/* Challenge modal */}
          {showChallengeModal && canDecide && (
            <Modal title={t('customer.approvalDetail.challengeModalTitle')} onClose={() => setShowChallengeModal(false)} size="sm">
              <p>{t('customer.approvalDetail.challengePrompt')}</p>
              <FieldLabel>{t('customer.approvals.approvedHours', { defaultValue: 'Hours you approve (0–24)' })}</FieldLabel>
              <HoursInput
                type="number" min="0" max="24" step="0.25"
                value={challengeHours}
                onChange={(e) => setChallengeHours(e.target.value)}
              />
              <HoursEquiv>= {formatHoursMinutes(challengeHours)}</HoursEquiv>
              <FieldLabel>{t('customer.approvals.challengeReason')}</FieldLabel>
              <ReasonTextarea
                value={challengeReason}
                onChange={(e) => setChallengeReason(e.target.value)}
                placeholder={t('customer.approvalDetail.challengePlaceholder')}
              />
              {modalError && <ModalError>{modalError}</ModalError>}
              <ModalActions>
                <Button
                  variant="outline"
                  onClick={() => setShowChallengeModal(false)}
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button variant="accent" onClick={handleChallenge} disabled={submitting}>
                  {submitting ? t('customer.approvalDetail.challenging') : t('customer.approvalDetail.challenge')}
                </Button>
              </ModalActions>
            </Modal>
          )}
        </>
      )}
    </>
  );
};

export default ApprovalDetail;
